import { useQuery } from '@tanstack/react-query';
import { useParams, useRouter } from '@tanstack/react-router';
import type { AdminApi } from '../../api/client.ts';
import { environmentsQuery, meQuery } from '../../api/queries.ts';

/** `environment`: permissions in the environment from the URL; `organization`: organization-level grants only. */
export type PermissionScope = 'environment' | 'organization';

export interface PermissionCheck {
  isPending: boolean;
  has(permission: string): boolean;
}

/** The server enforces every permission; the dashboard only hides or blocks what would be refused. */
export function usePermissions(scope: PermissionScope = 'environment'): PermissionCheck {
  const { api } = useRouter().options.context as { api: AdminApi };
  const { environmentId } = useParams({ strict: false }) as { environmentId?: string };
  const environments = useQuery({ ...environmentsQuery(api), enabled: scope === 'environment' });
  const me = useQuery({ ...meQuery(api), enabled: scope === 'organization' });
  const granted =
    scope === 'organization'
      ? me.data?.permissions
      : environments.data?.find((env) => env.id === environmentId)?.permissions;
  return {
    isPending: scope === 'organization' ? me.isPending : environments.isPending,
    has: (permission) => Boolean(granted?.includes(permission)),
  };
}
